import { getUserData, User } from "@shuttlerock/auth";
import { ConfigService } from "@shuttlerock/configuration";
import {
  initialize as ldClientInitialize,
  type LDClient,
  type LDContext,
  type LDContextCommon,
} from "launchdarkly-js-client-sdk";
import { commonBasicLogger } from "launchdarkly-js-sdk-common";

class Flags {
  public readonly client: Promise<LDClient>;

  constructor() {
    this.client = initClient();
  }

  private async isEnabled(flag: string, defaultValue = false): Promise<boolean> {
    await this.identifySignedInUser();
    const client = await this.client;
    return client.variation(flag, defaultValue);
  }

  /**
   * A flag answers for whoever is signed in now. The client starts from the session at boot,
   * which on the login callback can be no session yet, and the root identifies the user only
   * after the loaders that read flags have run.
   */
  private async identifySignedInUser(): Promise<void> {
    try {
      const data = await getUserData(new Request(""));
      await this.identifyUser(data?.user);
    } catch (error) {
      // The flag answers for the client's current context instead.
      console.warn("[Shuttlerock Cloud] Failed to identify LaunchDarkly user context.", error);
    }
  }

  public get conceptTracker() {
    return this.isEnabled("concept-tracker");
  }

  public get jamAdaptGenAi() {
    return this.isEnabled("jam-adapt-gen-ai");
  }

  public get scWorkflowCta() {
    return this.isEnabled("sc-workflow-cta");
  }

  public async identifyUser(user: User | undefined): Promise<void> {
    if (!user) {
      return;
    }

    if (!getUserContextKey(user)) {
      return;
    }

    const context = createUserContext(user);
    const client = await this.client;
    if (isSameContext(client.getContext(), context)) {
      return;
    }

    await client.identify(context);
  }
}

async function initClient() {
  const data = await getUserData(new Request(""));
  const context = data?.user
    ? createUserContext(data.user)
    : ({
        kind: "user",
        anonymous: true,
      } satisfies LDContext);

  if (typeof window !== "undefined" && data?.user) {
    const ldUserKey = data.user.principalKey || data.user.key;
    console.info("[LD] init context", {
      environment: ConfigService.ENVIRONMENT(),
      userKey: data.user.key,
      principalKey: data.user.principalKey,
      ldUserKey,
      tenantKey: data.user.tenantKey,
    });
  }

  const client = ldClientInitialize(ConfigService.LAUNCHDARKLY_CLIENT_ID() || "", context, {
    wrapperName: "react-client-sdk",
    sendEventsOnlyForVariation: true,
    logger: errorLogger(),
    bootstrap: "localStorage",
  });
  await client.waitUntilReady();

  return client;
}

function errorLogger() {
  return commonBasicLogger({
    destination: console.error,
    level: "error",
  });
}

function createUserContext(user: User): LDContext {
  const ldUserKey = getUserContextKey(user);
  return {
    kind: "multi",
    user: {
      kind: "user",
      key: ldUserKey,
      email: user.email,
    },
    ...(user.tenantKey && {
      tenant: {
        kind: "tenant",
        key: user.tenantKey,
      },
    }),
  };
}

function getUserContextKey(user: User): string | undefined {
  return user.principalKey || user.key;
}

type NormalizedLDUserContext = {
  key?: string;
  email?: unknown;
};

type NormalizedLDContext =
  | {
      kind: "multi";
      user: NormalizedLDUserContext;
      tenant?: {
        key?: string;
      };
    }
  | {
      kind?: string;
      key?: string;
      anonymous?: boolean;
    };

function isSameContext(current: LDContext, next: LDContext): boolean {
  return JSON.stringify(normalizeContext(current)) === JSON.stringify(normalizeContext(next));
}

function normalizeContext(context: LDContext): NormalizedLDContext {
  if ("kind" in context && context.kind === "multi") {
    return {
      kind: "multi",
      user: normalizeUserContext(context.user),
      ...(isContextCommon(context.tenant) && {
        tenant: {
          key: context.tenant.key,
        },
      }),
    };
  }

  const singleContext = context as LDContextCommon & { kind?: string };

  return {
    kind: singleContext.kind,
    key: singleContext.key,
    anonymous: singleContext.anonymous,
  };
}

function normalizeUserContext(context: unknown): NormalizedLDUserContext {
  if (!isContextCommon(context)) {
    return {};
  }

  return {
    key: context.key,
    email: context.email,
  };
}

function isContextCommon(context: unknown): context is LDContextCommon {
  return typeof context === "object" && context !== null && !Array.isArray(context);
}

// Client for determining the state of feature flags outside of React components
export const flags = new Flags();
